import { CapacityBar } from "@/lib/capacity";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Clock, AlertTriangle, Gauge } from "lucide-react";
import { setGrabbing } from "@/lib/pointerDnd";
import { toast } from "sonner";
import { TagChip, fmtMinutes } from "@/components/TaskShared";

const HOUR_START = 6;
const HOUR_END = 20; // exclusive-ish; last label at 20:00
const RANGES = [{ key: "week", label: "Week", days: 7, px: 56 }, { key: "3day", label: "3 Day", days: 3, px: 72 }, { key: "day", label: "Day", days: 1, px: 84 }];
const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const pad = (n) => String(n).padStart(2, "0");
const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const mondayOf = (iso) => { const d = new Date(iso + "T00:00:00"); const wd = (d.getDay() + 6) % 7; d.setDate(d.getDate() - wd); return isoOf(d); };
const addDaysIso = (iso, n) => { const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + n); return isoOf(d); };
const hhmmToMin = (s) => { const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(s || ""); return m ? +m[1] * 60 + +m[2] : null; };
const minToHHMM = (m) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
const min12 = (m) => { let h = Math.floor(m / 60), mm = m % 60; const ap = h >= 12 ? "pm" : "am"; h = h % 12 || 12; return mm ? `${h}:${pad(mm)}${ap}` : `${h}${ap}`; };
const snap15 = (m) => Math.round(m / 15) * 15;
const dayIdxOf = (iso) => (new Date(iso + "T00:00:00").getDay() + 6) % 7;
const rangeStart = (key, iso) => key === "week" ? mondayOf(iso) : key === "3day" ? addDaysIso(mondayOf(iso), Math.floor(dayIdxOf(iso) / 3) * 3) : iso;

function layoutDay(items) {
  const sorted = [...items].sort((a, b) => a._start - b._start || a._end - b._end);
  const laneEnds = [];
  sorted.forEach((it) => {
    let lane = laneEnds.findIndex((e) => e <= it._start);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(it._end); } else { laneEnds[lane] = it._end; }
    it._lane = lane;
  });
  sorted.forEach((it) => {
    const overlaps = sorted.filter((o) => o._start < it._end && o._end > it._start);
    it._cols = Math.max(...overlaps.map((o) => o._lane)) + 1;
  });
  return sorted;
}

function blockColor(t) {
  if (t.is_completed || t.status === "Completed") return { bg: "#e9f7ef", bar: "#30BD79", text: "#146c43" };
  if (t.status === "To Do") return { bg: "#F1F1F4", bar: "#9296A0", text: "#3d4148" };
  return { bg: "#EDE7FF", bar: "#723FFF", text: "#4a2bb0" };
}

export function WeekSchedule({ tasks, me, canEdit = true, onOpenTask, onReschedule, capacity }) {
  const [rangeKey, setRangeKey] = useState(() => { try { return localStorage.getItem("mc_cal_range") || "3day"; } catch (e) { return "3day"; } });
  const range = RANGES.find((r) => r.key === rangeKey) || RANGES[1];
  const SPAN = range.days, PX_PER_HOUR = range.px, PX_PER_MIN = PX_PER_HOUR / 60;
  const todayIso = isoOf(new Date());
  const [weekStart, setWeekStart] = useState(() => rangeStart(rangeKey, todayIso));
  const pickRange = (k) => { setRangeKey(k); setWeekStart(rangeStart(k, weekStart <= todayIso && todayIso <= addDaysIso(weekStart, SPAN - 1) ? todayIso : weekStart)); try { localStorage.setItem("mc_cal_range", k); } catch (e) {} };
  const [drag, setDrag] = useState(null); // {id, dayIdx, startMin, dur, ghostDay, ghostStart, moved}
  const colsRef = useRef(null);
  const weekDays = useMemo(() => Array.from({ length: SPAN }, (_, i) => addDaysIso(weekStart, i)), [weekStart, SPAN]);
  const roomy = SPAN <= 3;
  const sched = me?.schedule_hours || null;
  const hours = Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => HOUR_START + i);

  const byDay = useMemo(() => {
    const map = {};
    weekDays.forEach((d) => (map[d] = []));
    (tasks || []).forEach((t) => {
      if (!t.due_date || t.is_completed) return;
      if (!(t.due_date in map)) return;
      const start = hhmmToMin(t.start_time) ?? 11 * 60;
      const dur = Math.max(15, t.time_estimate_minutes || 30);
      map[t.due_date].push({ ...t, _start: start, _end: start + dur, _dur: dur });
    });
    Object.keys(map).forEach((d) => (map[d] = layoutDay(map[d])));
    return map;
  }, [tasks, weekDays]);

  const gridHeight = (HOUR_END - HOUR_START) * PX_PER_HOUR;
  const dayLoad = useMemo(() => {
    const cap = me?.capacity_days || {};
    const m = {};
    weekDays.forEach((d) => {
      const scheduled = (byDay[d] || []).reduce((s, t) => s + (t._dur || 0), 0);
      const capMin = Math.round((cap[DAY_KEYS[dayIdxOf(d)]] || 0) * 60);
      m[d] = { scheduled, capMin, over: scheduled > 0 && scheduled > capMin };
    });
    return m;
  }, [byDay, weekDays, me]);
  const monthLabel = () => {
    const a = new Date(weekStart + "T00:00:00"), b = new Date(weekDays[SPAN - 1] + "T00:00:00");
    const f = (d) => d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
    if (SPAN === 1) return a.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    return `${f(a)} – ${f(b)}, ${b.getFullYear()}`;
  };

  const cancelDrag = () => { setDrag(null); setGrabbing(false); };
  const dragging = !!drag;
  useEffect(() => {
    if (!dragging) return undefined;
    const onKey = (e) => { if (e.key === "Escape") cancelDrag(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dragging]);
  const workBlocks = (d) => {
    const raw = sched ? sched[DAY_KEYS[dayIdxOf(d)]] : null;
    return (!raw ? [] : (Array.isArray(raw) ? raw : [raw])).filter((b) => b && b.start && b.end);
  };

  const onPointerDown = (e, t, dayIdx) => {
    if (!canEdit) return;
    e.preventDefault();
    setDrag({ id: t.id, dayIdx, startMin: t._start, dur: t._dur, startX: e.clientX, startY: e.clientY, ghostDay: dayIdx, ghostStart: t._start, moved: false });
  };
  const onPointerMove = (e) => {
    if (!drag) return;
    const dx = e.clientX - drag.startX, dy = e.clientY - drag.startY;
    const moved = drag.moved || Math.abs(dx) > 4 || Math.abs(dy) > 4;
    if (moved && !drag.moved) setGrabbing(true);
    const colW = colsRef.current ? colsRef.current.getBoundingClientRect().width / SPAN : 120;
    let day = drag.dayIdx + Math.round(dx / colW);
    day = Math.max(0, Math.min(SPAN - 1, day));
    let start = snap15(drag.startMin + Math.round(dy / PX_PER_MIN));
    start = Math.max(HOUR_START * 60, Math.min(HOUR_END * 60 - drag.dur, start));
    setDrag({ ...drag, moved, ghostDay: day, ghostStart: start });
  };
  const onPointerUp = () => {
    if (!drag) return;
    const d = drag; setDrag(null); setGrabbing(false);
    if (!d.moved) { onOpenTask && onOpenTask(d.id); return; }
    const newDue = weekDays[d.ghostDay];
    const newStart = minToHHMM(d.ghostStart);
    const origDue = weekDays[d.dayIdx];
    if (newDue === origDue && d.ghostStart === d.startMin) return;
    if (sched && workBlocks(newDue).length === 0) { toast.error(`${me?.name || "They"} isn't scheduled to work that day`); return; }
    onReschedule && onReschedule(d.id, { due_date: newDue, start_time: newStart });
  };

  return (
    <div data-testid="week-schedule" onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={() => drag && cancelDrag()}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button data-testid="week-prev" onClick={() => setWeekStart(addDaysIso(weekStart, -SPAN))} className="h-8 w-8 rounded-full border border-[#EEEFEF] bg-white flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD]"><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="week-today" onClick={() => setWeekStart(rangeStart(rangeKey, todayIso))} className="h-8 px-3 rounded-full border border-[#EEEFEF] bg-white text-xs font-medium text-[#595D62] hover:bg-[#F6F6FD]">Today</button>
          <button data-testid="week-next" onClick={() => setWeekStart(addDaysIso(weekStart, SPAN))} className="h-8 w-8 rounded-full border border-[#EEEFEF] bg-white flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD]"><ChevronRight className="h-4 w-4" /></button>
          <span data-testid="week-label" className="ml-1 font-display text-lg font-semibold text-[#06060C]">{monthLabel()}</span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-xs text-[#9FA1A4] flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {canEdit ? "Drag a task to reschedule · click to open" : "Click a task to open"}</p>
          <div className="inline-flex items-center gap-0.5 rounded-full border border-[#EEEFEF] bg-white p-0.5" data-testid="cal-range-switch">
            {RANGES.map((r) => (
              <button key={r.key} data-testid={`cal-range-${r.key}`} onClick={() => pickRange(r.key)} className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${rangeKey === r.key ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}>{r.label}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-[#EEEFEF] bg-white overflow-hidden">
        <div className="grid" style={{ gridTemplateColumns: `56px repeat(${SPAN}, 1fr)` }}>
          <div className="border-b border-r border-[#f0f0f3]" />
          {weekDays.map((d, i) => {
            const dd = new Date(d + "T00:00:00");
            const isToday = d === todayIso;
            const ld = dayLoad[d] || {};
            return (
              <div key={d} data-testid={`week-day-head-${d}`} className={`border-b border-[#f0f0f3] px-2 py-2 text-center transition-colors ${i < SPAN - 1 ? "border-r" : ""} ${drag?.moved && drag.ghostDay === i ? (sched && workBlocks(d).length === 0 ? "bg-[#FDECEC]" : "bg-[#EDE7FF]") : ld.over ? "bg-[#FDECEC]" : isToday ? "bg-[#F6F2FF]" : ""}`}>
                <div className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">{DAY_LABELS[dayIdxOf(d)]}</div>
                <div className={`text-sm font-semibold ${isToday ? "text-[#5b2fd6]" : "text-[#06060C]"}`}>{dd.getDate()}</div>
                {capacity && (() => { const c = capacity.get(d); const mv = drag?.moved && drag.ghostDay !== drag.dayIdx ? (capacity.drawOf(drag.id) || 0) : 0; const add = drag?.moved && drag.ghostDay === i ? mv : drag?.moved && drag.dayIdx === i ? -mv : 0; return <CapacityBar compact used={c.used + add} cap={c.cap} testid={`week-capacity-${d}`} />; })()}
                {!capacity && ld.over && <div data-testid={`week-overload-${d}`} title={`Scheduled ${fmtMinutes(ld.scheduled)} vs ${fmtMinutes(ld.capMin)} capacity`} className="mt-0.5 inline-flex items-center gap-0.5 text-[9px] font-semibold text-[#c0392b] leading-none"><AlertTriangle className="h-2.5 w-2.5" /> {fmtMinutes(ld.scheduled)}/{fmtMinutes(ld.capMin)}</div>}
              </div>
            );
          })}
        </div>
        <div className="grid" style={{ gridTemplateColumns: "56px 1fr" }}>
          <div className="relative" style={{ height: gridHeight }}>
            {hours.map((h, i) => (
              <div key={h} className="absolute right-2 -translate-y-1/2 font-mono-data text-[10px] text-[#c4c5cc]" style={{ top: i * PX_PER_HOUR }}>{pad(h)}:00</div>
            ))}
          </div>
          <div ref={colsRef} className="relative grid" style={{ gridTemplateColumns: `repeat(${SPAN}, 1fr)`, height: gridHeight }}>
            {hours.slice(0, -1).map((h, i) => (
              <div key={h} className="absolute left-0 right-0 border-t border-[#f4f4f7]" style={{ top: (i + 1) * PX_PER_HOUR }} />
            ))}
            {weekDays.map((d, dayIdx) => {
              const blocks = workBlocks(d);
              const offDay = !!sched && blocks.length === 0;
              const shades = blocks.map((b) => {
                const ws = Math.max(HOUR_START * 60, hhmmToMin(b.start) ?? 0);
                const we = Math.min(HOUR_END * 60, hhmmToMin(b.end) ?? 0);
                return we > ws ? { top: (ws - HOUR_START * 60) * PX_PER_MIN, height: (we - ws) * PX_PER_MIN } : null;
              }).filter(Boolean);
              return (
                <div key={d} className={`relative transition-colors ${dayIdx < SPAN - 1 ? "border-r border-[#f4f4f7]" : ""} ${drag?.moved && drag.ghostDay === dayIdx ? (offDay ? "bg-[#FDECEC]" : "bg-[#F4EFFF]") : d === todayIso ? "bg-[#FBF9FF]" : ""}`}>
                  {shades.map((shade, si) => <div key={si} className="absolute left-0 right-0 bg-[#F1ECFF]/60" style={{ top: shade.top, height: shade.height }} title="Working hours" />)}
                  {drag?.moved && drag.ghostDay === dayIdx && (
                    <div data-testid="week-drop-preview" data-off-day={offDay ? "true" : undefined} className={`absolute left-1 right-1 z-20 rounded-md border-2 border-dashed pointer-events-none flex items-start px-2 py-1 ${offDay ? "border-[#e5484d] bg-[#e5484d]/10" : "border-[#723FFF] bg-[#723FFF]/10"}`} style={{ top: (drag.ghostStart - HOUR_START * 60) * PX_PER_MIN, height: Math.max(drag.dur * PX_PER_MIN, 22) }}>
                      <span className={`font-mono-data text-[10px] font-semibold bg-white/90 rounded px-1 ${offDay ? "text-[#c0392b]" : "text-[#5b2fd6]"}`}>{offDay ? "Not a working day" : `${new Date(d + "T00:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "numeric" })} · ${min12(drag.ghostStart)}–${min12(drag.ghostStart + drag.dur)}`}</span>
                    </div>
                  )}
                  {(byDay[d] || []).map((t) => {
                    const isDragging = drag && drag.moved && drag.id === t.id;
                    const top = (t._start - HOUR_START * 60) * PX_PER_MIN;
                    const height = Math.max(t._dur * PX_PER_MIN, 22);
                    const c = blockColor(t);
                    const w = 100 / (t._cols || 1);
                    const left = (t._lane || 0) * w;
                    return (
                      <div key={t.id} data-testid={`week-task-${t.id}`}
                        onPointerDown={(e) => onPointerDown(e, t, dayIdx)}
                        className={`absolute overflow-hidden shadow-sm rounded-r-md transition-opacity ${canEdit ? "cursor-grab active:cursor-grabbing select-none" : "cursor-pointer"} ${isDragging ? "z-10 opacity-35" : "z-10 hover:shadow-md"}`}
                        style={{ top, height, left: `calc(${left}% + 2px)`, width: `calc(${w}% - 4px)`, background: c.bg, borderLeft: `3px solid ${c.bar}` }}>
                        <div className={`${roomy ? "px-2.5 py-1.5" : "px-1.5 py-1"} leading-tight`}>
                          <div className={`font-mono-data ${roomy ? "text-[10px]" : "text-[9px]"} flex items-center gap-1.5`} style={{ color: c.bar }}>
                            <span>{min12(t._start)}{roomy ? `–${min12(t._end)}` : ""} · {t.code}</span>
                            {roomy && t.time_estimate_minutes ? <span className="inline-flex items-center gap-0.5 text-[#9296A0]"><Gauge className="h-2.5 w-2.5" />{fmtMinutes(t.time_estimate_minutes)}</span> : null}
                          </div>
                          <div className={`${roomy ? "text-[13px] line-clamp-2" : "text-[11px] truncate"} font-medium`} style={{ color: c.text }}>{t.title}</div>
                          {height > (roomy ? 56 : 46) && <div className="mt-0.5 flex items-center gap-1 flex-wrap">{t.tag && <TagChip tag={t.tag} />}{(roomy ? [t.project_name || t.project?.name, t.client?.name].filter(Boolean).join(" · ") : t.client?.name) && <span className={`${roomy ? "text-[10px]" : "text-[9px]"} text-[#9296A0] truncate`}>{roomy ? [t.project_name || t.project?.name, t.client?.name].filter(Boolean).join(" · ") : t.client.name}</span>}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
